import { useEffect, useState } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CreditCard, Sparkles } from "lucide-react";
import { LogoMark } from "@/components/Logo";

// Paths users may hit even without an active subscription.
const OPEN_PATHS = ["/billing", "/billing/success", "/billing/cancel", "/login", "/register", "/auth/callback"];

const BILLING_ENABLED = String(process.env.REACT_APP_BILLING_ENABLED || "true").toLowerCase() !== "false";

export default function BillingGate({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!user || !BILLING_ENABLED) { setLoading(false); return; }
      try {
        const { data } = await api.get("/billing/subscription");
        if (!cancelled) setSub(data);
      } catch {
        if (!cancelled) setSub({ status: "none" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [user, loc.pathname]);

  if (!BILLING_ENABLED) return children;
  if (!user) return children;
  if (loading) return children; // don't block first paint

  const isOpen = OPEN_PATHS.some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"));
  const activeOk = sub?.grandfathered || ["active", "trialing"].includes(sub?.status);

  if (isOpen || activeOk) return children;

  // No plan yet — mandatory pick screen.
  if (!sub?.status || sub.status === "none") {
    return <Navigate to="/billing" replace state={{ from: loc }} />;
  }

  // Lapsed — hard lockout screen (admins get the reactivate CTA).
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full border-2 border-border bg-card p-8 text-center space-y-4">
        <AlertTriangle className="h-10 w-10 text-red-600 mx-auto" />
        <LogoMark size={36} className="text-foreground mx-auto" />
        <h1 className="font-heading text-2xl font-black tracking-tight">Billing is paused</h1>
        <p className="text-sm text-muted-foreground">
          {sub?.status === "past_due"
            ? "Your last payment didn't go through. Update your card and we'll pick right back up."
            : "Your subscription is currently inactive. Reactivate to start logging dispatches again."}
        </p>
        {sub?.is_admin ? (
          <div className="space-y-2">
            <Button asChild className="rounded-none w-full h-11 bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-[0.15em] text-xs font-bold">
              <Link to="/billing" data-testid="reactivate-billing"><CreditCard className="h-4 w-4 mr-2" /> Reactivate billing</Link>
            </Button>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            Ask a team admin to reactivate the subscription in the Billing page.
          </div>
        )}
      </div>
    </div>
  );
}

export function TrialBanner() {
  const [sub, setSub] = useState(null);
  useEffect(() => {
    if (!BILLING_ENABLED) return;
    api.get("/billing/subscription").then(({ data }) => setSub(data)).catch(() => {});
  }, []);
  if (!BILLING_ENABLED) return null;
  if (!sub || sub.grandfathered || sub.status !== "trialing" || !sub.trial_end) return null;
  const daysLeft = Math.max(0, Math.ceil((new Date(sub.trial_end) - new Date()) / (1000 * 60 * 60 * 24)));
  return (
    <div data-testid="trial-banner" className="border-2 border-orange-600 bg-orange-50 dark:bg-orange-950 px-4 py-2 flex items-center gap-3 text-sm">
      <Sparkles className="h-4 w-4 text-orange-600 shrink-0" />
      <div className="flex-1">
        <strong>Trial active</strong> · {daysLeft} day{daysLeft === 1 ? "" : "s"} left before your card is charged.
      </div>
      <Link to="/billing" className="text-xs uppercase tracking-[0.15em] font-bold underline">Manage plan</Link>
    </div>
  );
}
